/**
 * i18n setup — 4 ngôn ngữ: vi (default), en, zh-CN, ru.
 *
 * Quy ước:
 *   - `LOCALE_STORAGE_KEY` persist `localStorage`.
 *   - Auto-detect browser language làm fallback nếu user chưa chọn.
 *   - Tên món + ingredient: GIỮ tiếng Việt trong mọi locale (bản sắc).
 *
 * Tham chiếu:
 *   - https://react.i18next.com/
 *   - locale files: `src/locales/{vi,en,zh-CN,ru}/common.json`
 */
import i18n from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import { initReactI18next } from "react-i18next";

import en from "../locales/en/common.json";
import ru from "../locales/ru/common.json";
import vi from "../locales/vi/common.json";
import zhCN from "../locales/zh-CN/common.json";

export const SUPPORTED_LOCALES = ["vi", "en", "zh-CN", "ru"] as const;
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

export const LOCALE_STORAGE_KEY = "cafechuquan_locale";

export const LOCALE_NATIVE_NAME: Record<SupportedLocale, string> = {
  vi: "Tiếng Việt",
  en: "English",
  "zh-CN": "中文",
  ru: "Русский",
};

export const LOCALE_FLAG: Record<SupportedLocale, string> = {
  vi: "🇻🇳",
  en: "🇬🇧",
  "zh-CN": "🇨🇳",
  ru: "🇷🇺",
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      vi: { common: vi },
      en: { common: en },
      "zh-CN": { common: zhCN },
      ru: { common: ru },
    },
    fallbackLng: "vi",
    defaultNS: "common",
    interpolation: { escapeValue: false }, // React đã escape
    detection: {
      order: ["localStorage", "navigator"],
      lookupLocalStorage: LOCALE_STORAGE_KEY,
      caches: ["localStorage"],
    },
    supportedLngs: [...SUPPORTED_LOCALES],
  });

/** Đổi ngôn ngữ + persist. */
export function setLocale(locale: SupportedLocale): void {
  void i18n.changeLanguage(locale);
}

/** Trả locale hiện tại — luôn là 1 trong 4 supported. */
export function getCurrentLocale(): SupportedLocale {
  const lang = i18n.language;
  if ((SUPPORTED_LOCALES as readonly string[]).includes(lang)) {
    return lang as SupportedLocale;
  }
  // Fallback: nếu user lang = "en-US" → match "en", "zh" → match "zh-CN".
  const base = lang.split("-")[0];
  for (const supported of SUPPORTED_LOCALES) {
    if (supported.split("-")[0] === base) return supported;
  }
  return "vi";
}

export default i18n;
